import { useEffect, useState } from 'react';
import { marketplaceApi, type PaymentOptions, type ProductCard } from '../api/marketplace';
import { useCart } from '../features/cart/CartContext';
import { PaymentSheet } from '../features/checkout/PaymentSheet';
import { notifyPayResult, payErrorMessage, payItems } from '../features/checkout/pay';
import { StarIcon } from '../features/tabs/icons';
import { TopUpBlock } from '../features/topup/TopUpBlock';
import { bindBackButton, haptic, hapticImpact, hideBackButton } from '../lib/telegram';
import { ErrorState, Loader } from '../components/States';
import type { CheckoutPlatform } from '../api/marketplace';
import { isTopUpProduct } from '../lib/top-up';
import { pricedFrom, selectedEdition } from '../lib/editions';
import { RatingStars, ReviewsBlock } from '../features/reviews/ReviewsBlock';
import './ProductPage.css';

interface ProductPageProps {
  productId: string;
  initialEditionId?: string;
  payments: PaymentOptions;
  onClose: () => void;
  onOpenCart: () => void;
  onPurchased: (orderId?: string) => void;
}

export function ProductPage({
  productId,
  initialEditionId,
  payments,
  onClose,
  onOpenCart,
  onPurchased,
}: ProductPageProps) {
  const cart = useCart();
  const [product, setProduct] = useState<ProductCard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [editionId, setEditionId] = useState(initialEditionId ?? '');

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError(null);

    marketplaceApi
      .product(productId)
      .then((item) => {
        if (!cancelled) {
          setProduct(item);
          if (!editionId) setEditionId(item.editions?.[0]?.id ?? initialEditionId ?? '');
        }
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause instanceof Error ? cause.message : 'Не удалось открыть товар');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [productId]);

  useEffect(() => {
    if (payOpen) return undefined;
    return bindBackButton(() => {
      hideBackButton();
      onClose();
    });
  }, [onClose, payOpen]);

  const editions = product?.editions ?? [];
  const edition = product ? selectedEdition(product, editionId) : null;
  const prices = product ? pricedFrom(product, edition) : null;
  const cardDescription = edition?.description || product?.description || null;
  const qty = product
    ? (cart.lines.find((line) => line.productId === product.id && (line.editionId ?? '') === (edition?.id ?? ''))
        ?.quantity ?? 0)
    : 0;
  const cap = product ? Math.min(product.maxPerOrder, Math.max(product.availableCount, qty)) : 0;
  const topUp = product ? isTopUpProduct(product) : false;

  const addOne = (): void => {
    if (!product || !product.inStock || qty >= cap) return;
    if (qty === 0) cart.add(product, 1, edition?.id);
    else cart.setQuantity(product.id, qty + 1, edition?.id);
    hapticImpact('light');
  };

  const removeOne = (): void => {
    if (!product || qty <= 0) return;
    cart.setQuantity(product.id, qty - 1, edition?.id);
    hapticImpact('light');
  };

  const pay = async (platform: CheckoutPlatform): Promise<void> => {
    if (!product) return;
    setBusy(true);
    setError(null);

    try {
      const outcome = await payItems(
        [
          {
            productId: product.id,
            quantity: Math.max(1, qty),
            ...(edition?.id ? { editionId: edition.id } : {}),
          },
        ],
        platform,
      );
      const message = notifyPayResult(outcome.result);
      if (message) setError(message);
      if (outcome.result === 'paid') onPurchased(outcome.orderId);
      else if (outcome.result === 'pending') onPurchased();
      if (outcome.result === 'paid') onClose();
    } catch (cause) {
      haptic('error');
      setError(payErrorMessage(cause));
    } finally {
      setBusy(false);
      setPayOpen(false);
    }
  };

  return (
    <div className="product-page">
      <button
        type="button"
        className="product-page__back"
        onClick={() => {
          hideBackButton();
          onClose();
        }}
      >
        ← Назад
      </button>

      {isLoading ? <Loader /> : null}
      {error && !product ? <ErrorState message={error} onRetry={onClose} /> : null}

      {product && topUp ? (
        <TopUpBlock
          products={[product]}
          payments={payments}
          onPurchased={(orderId) => {
            onPurchased(orderId);
            onClose();
          }}
        />
      ) : null}

      {product && !topUp ? (
        <article className="product-page__card">
          <div className="product-page__cover">
            {product.coverUrl ? (
              <img src={product.coverUrl} alt="" />
            ) : (
              <span>{product.title.slice(0, 2).toUpperCase()}</span>
            )}
            {prices?.compareAtStars || prices?.compareAtRub ? <span className="product-page__sale">Акция</span> : null}
          </div>

          <h1 className="product-page__title">{product.title}</h1>
          {product.subtitle ? <p className="product-page__subtitle">{product.subtitle}</p> : null}
          {product.ratingCount ? (
            <p className="product-page__rating">
              <RatingStars value={product.ratingAvg ?? 0} />
              <span>
                {product.ratingAvg} · {product.ratingCount}
              </span>
            </p>
          ) : null}

          {editions.length > 0 ? (
            <div className="editions" role="group" aria-label="Издание">
              {editions.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`editions__chip${edition?.id === item.id ? ' editions__chip--active' : ''}`}
                  onClick={() => setEditionId(item.id)}
                >
                  {item.title}
                </button>
              ))}
            </div>
          ) : null}

          {prices?.priceRub ? (
            <p className="product-page__price">
              {prices.priceRub} ₽
              {prices.compareAtRub ? <s>{prices.compareAtRub} ₽</s> : null}
            </p>
          ) : null}
          <div className="product-page__price">
            <StarIcon size={22} />
            <span>{prices?.priceStars}</span>
            {prices?.compareAtStars ? <s>{prices.compareAtStars}</s> : null}
          </div>

          <p className="product-page__stock">
            {product.inStock
              ? product.stockQuantity == null
                ? 'В наличии'
                : `В наличии: ${product.availableCount}`
              : 'Нет в наличии'}
          </p>

          {error && product ? <p className="product-page__error">{error}</p> : null}

          <div className="product-page__text">
            {cardDescription ? (
              cardDescription.split('\n').map((paragraph, index) => (
                <p key={`${edition?.id ?? 'product'}-${index}-${paragraph.slice(0, 24)}`}>{paragraph}</p>
              ))
            ) : (
              <p>Описание появится позже</p>
            )}
          </div>

          <div className="product-page__actions">
            <button
              type="button"
              className="product-page__buy"
              disabled={!product.inStock || busy}
              onClick={() => setPayOpen(true)}
            >
              Приобрести
            </button>
            <div className="product-page__cart-bar">
              <div className="product-page__stepper" aria-label="Количество в корзине">
                <button type="button" disabled={qty <= 0} onClick={removeOne} aria-label="Убрать штуку">
                  −
                </button>
                <span>{qty}</span>
                <button
                  type="button"
                  disabled={!product.inStock || qty >= cap}
                  onClick={addOne}
                  aria-label="Добавить штуку"
                >
                  +
                </button>
              </div>
              <button type="button" className="product-page__cart-open" onClick={onOpenCart}>
                Корзина{cart.count > 0 ? ` · ${cart.count}` : ''}
              </button>
            </div>
          </div>
        </article>
      ) : null}

      {product ? <ReviewsBlock productId={product.id} /> : null}

      {product && !topUp ? (
        <PaymentSheet
          open={payOpen}
          payments={{ ...payments, tribute: payments.tribute || Boolean(product.hasTribute) }}
          busy={busy}
          starsAmount={(prices?.priceStars ?? 0) * Math.max(1, qty)}
          rubAmount={prices?.priceRub ? prices.priceRub * Math.max(1, qty) : undefined}
          onClose={() => setPayOpen(false)}
          onPay={(platform) => void pay(platform)}
        />
      ) : null}
    </div>
  );
}
